import Lang from '../../global/Lang';
import ColorSelectionOption from '../../base/option/ColorSelectionOption';
import Color from '../../base/option/Color';

enum SpaceMemberRole {
  PREVIEW_ONLY = 'PREVIEW_ONLY',
  UPLOAD_PREVIEW = 'UPLOAD_PREVIEW',
  READ_ONLY = 'READ_ONLY',
  READ_WRITE = 'READ_WRITE',
  ADMIN = 'ADMIN',
}

const SpaceMemberRoles: SpaceMemberRole[] = Object.keys(SpaceMemberRole).map(
  (k) => k as SpaceMemberRole
);

// 扩展 ColorSelectionOption 以包含 description 属性
interface SpaceMemberRoleOption extends ColorSelectionOption {
  description?: string;
}

// 核心修改：将所有静态常量改为函数
const getSpaceMemberRoleMap = (): Record<SpaceMemberRole, SpaceMemberRoleOption> => ({
  [SpaceMemberRole.PREVIEW_ONLY]: {
    name: Lang.t('space.memberRolePreviewOnly'),
    value: SpaceMemberRole.PREVIEW_ONLY,
    color: Color.INFO,
    description: Lang.t('space.memberRolePreviewOnlyDesc', '只能预览文件，不能上传或下载')
  },
  [SpaceMemberRole.UPLOAD_PREVIEW]: {
    name: Lang.t('space.memberRoleUploadPreview'),
    value: SpaceMemberRole.UPLOAD_PREVIEW,
    color: Color.WARNING,
    description: Lang.t('space.memberRoleUploadPreviewDesc', '可以上传文件和预览文件')
  },
  [SpaceMemberRole.READ_ONLY]: {
    name: Lang.t('space.memberRoleReadonly'),
    value: SpaceMemberRole.READ_ONLY,
    color: Color.SUCCESS,
    description: Lang.t('space.memberRoleReadonlyDesc', '只能查看文件，不能编辑')
  },
  [SpaceMemberRole.READ_WRITE]: {
    name: Lang.t('space.memberRoleReadWrite'),
    value: SpaceMemberRole.READ_WRITE,
    color: Color.PRIMARY,
    description: Lang.t('space.memberRoleReadWriteDesc', '可以查看和编辑文件')
  },
  [SpaceMemberRole.ADMIN]: {
    name: Lang.t('space.memberRoleAdmin'),
    value: SpaceMemberRole.ADMIN,
    color: Color.DANGER,
    description: Lang.t('space.memberRoleAdminDesc', '空间管理员，拥有所有权限')
  },
});

// 修改为函数形式
const getSpaceMemberRoleList = (): SpaceMemberRoleOption[] => {
  return SpaceMemberRoles.map((type: SpaceMemberRole) => getSpaceMemberRoleMap()[type]);
};

// 获取角色描述
const getSpaceMemberRoleDescription = (role: SpaceMemberRole): string => {
  return getSpaceMemberRoleMap()[role]?.description || '';
};

// 检查是否为有效的角色
const isValidSpaceMemberRole = (role: string): role is SpaceMemberRole => {
  return Object.values(SpaceMemberRole).includes(role as SpaceMemberRole);
};

// 将字符串转换为 SpaceMemberRole
const toSpaceMemberRole = (role: string): SpaceMemberRole | null => {
  if (isValidSpaceMemberRole(role)) {
    return role as SpaceMemberRole;
  }
  return null;
};

// 获取角色的显示名称
const getSpaceMemberRoleDisplayName = (role: SpaceMemberRole): string => {
  return getSpaceMemberRoleMap()[role]?.name || role;
};

// 获取角色的颜色
const getSpaceMemberRoleColor = (role: SpaceMemberRole): string => {
  return getSpaceMemberRoleMap()[role]?.color || Color.PRIMARY;
};

// 检查角色是否具有特定权限
const hasRolePermission = (role: SpaceMemberRole, requiredPermission: string): boolean => {
  // 权限层级定义（从低到高）
  const permissionHierarchy: Record<SpaceMemberRole, number> = {
    [SpaceMemberRole.PREVIEW_ONLY]: 1,
    [SpaceMemberRole.UPLOAD_PREVIEW]: 2,
    [SpaceMemberRole.READ_ONLY]: 3,
    [SpaceMemberRole.READ_WRITE]: 4,
    [SpaceMemberRole.ADMIN]: 5,
  };

  // 权限映射
  const permissionMap: Record<string, SpaceMemberRole[]> = {
    'PREVIEW': [SpaceMemberRole.PREVIEW_ONLY, SpaceMemberRole.UPLOAD_PREVIEW, SpaceMemberRole.READ_ONLY, SpaceMemberRole.READ_WRITE, SpaceMemberRole.ADMIN],
    'UPLOAD': [SpaceMemberRole.UPLOAD_PREVIEW, SpaceMemberRole.READ_WRITE, SpaceMemberRole.ADMIN],
    'READ': [SpaceMemberRole.READ_ONLY, SpaceMemberRole.READ_WRITE, SpaceMemberRole.ADMIN],
    'WRITE': [SpaceMemberRole.READ_WRITE, SpaceMemberRole.ADMIN],
    'ADMIN': [SpaceMemberRole.ADMIN],
    'MANAGE_MEMBERS': [SpaceMemberRole.ADMIN],
  };

  // 检查特定权限
  if (permissionMap[requiredPermission]) {
    return permissionMap[requiredPermission].includes(role);
  }

  // 默认权限检查
  return permissionHierarchy[role] >= permissionHierarchy[requiredPermission as SpaceMemberRole] || false;
};

// 获取所有角色选项（用于下拉选择等）
const getAllRoleOptions = (): Array<{label: string; value: SpaceMemberRole; color: string; description: string}> => {
  return SpaceMemberRoles.map(role => ({
    label: getSpaceMemberRoleMap()[role].name,
    value: role,
    color: getSpaceMemberRoleMap()[role].color,
    description: getSpaceMemberRoleMap()[role].description || ''
  }));
};

// 比较两个角色的权限级别
const compareRoles = (role1: SpaceMemberRole, role2: SpaceMemberRole): number => {
  const hierarchy: Record<SpaceMemberRole, number> = {
    [SpaceMemberRole.PREVIEW_ONLY]: 1,
    [SpaceMemberRole.UPLOAD_PREVIEW]: 2,
    [SpaceMemberRole.READ_ONLY]: 3,
    [SpaceMemberRole.READ_WRITE]: 4,
    [SpaceMemberRole.ADMIN]: 5,
  };
  
  return hierarchy[role1] - hierarchy[role2];
};

// 检查是否具有足够权限
const hasMinimumRole = (userRole: SpaceMemberRole, requiredRole: SpaceMemberRole): boolean => {
  return compareRoles(userRole, requiredRole) >= 0;
};

// 导出
export {
  SpaceMemberRole,
  SpaceMemberRoles,
  getSpaceMemberRoleMap,           // 改为函数
  getSpaceMemberRoleList,          // 改为函数
  getSpaceMemberRoleDescription,
  isValidSpaceMemberRole,
  toSpaceMemberRole,
  getSpaceMemberRoleDisplayName,
  getSpaceMemberRoleColor,
  hasRolePermission,
  getAllRoleOptions,
  compareRoles,
  hasMinimumRole,
};

export type { SpaceMemberRoleOption };